<template>
  <input
    v-bind="$attrs"
    class="currency"
    :name="name"
    ref="inputRef"
    type="text"
    :value="formattedValue"
    :disabled="disabled"
  />
  <slot name="errors"></slot>
</template>

<script setup lang="ts">
import { watch, onMounted, ref, onBeforeMount, computed, toRef } from "vue";
import { useCurrencyInput, CurrencyInputOptions } from "vue-currency-input";
import { useField, ErrorMessage } from "vee-validate";
import { Schema, object } from "yup";

interface Props {
  name: string;
  options?: CurrencyInputOptions;
  initialValue?: string | number | null;
  modelValue?: string | number | null | object | unknown;
  mode?: "add" | "edit" | "view";
  rules?: Schema;
  disabled: boolean;
}

interface Emits {
  (eventName: "change", value: unknown | number | null | string): void;
  (
    eventName: "update:modelValue",
    value: unknown | number | null | string
  ): void;
}

const props = defineProps<Props>();
const name = toRef(props, "name");
const modelValue = toRef(props, "modelValue");

const defaultOptions = ref({
  currency: "USD",
  locale: "en-US",
  precision: 2,
});

const emit = defineEmits<Emits>();

const { formattedValue, numberValue, inputRef, setValue } = useCurrencyInput(
  props.options || defaultOptions.value
);

const { handleChange, value, errors, validate, resetField } = useField(
  name.value || "",
  props.rules || object({})
);

watch(numberValue, (newValue, oldValue) => {
  // sync it with vee-validate, this will trigger validation
  handleChange(newValue);
});

watch(modelValue, (newValue, oldValue) => {
  if (newValue === "" || newValue === null || newValue === undefined) {
    setValue(null);
  } else {
    setValue(Number(value.value));
  }
});

defineExpose({
  handleChange,
  value,
  formattedValue,
  inputRef,
  setValue,
  validate,
  name,
  resetField,
});
</script>

<style lang="css" scoped>
input[type="text"].currency {
  min-width: 6rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  height: 46px;
  font-size: 14px;
}

input[type="text"].currency:disabled,
input[type="text"].currency.disabled {
}

ul.errors {
  padding: 0;
  margin: 0;
  list-style-type: none;
}

ul.errors li {
  font-size: 12px;
  color: red;
}
</style>
